import { useI18n } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import { Button } from '@ecom/ui-kit';
import { type ReactElement } from 'react';

import { useSetProductFeaturedMutation } from '../api';
import { MAX_FEATURED_PRODUCTS, type Product } from '../types';

import { useToast } from '@/components/ui/toast/ToastProvider';
import { ApiError } from '@/lib/api-client';

function StarIcon({ filled }: { filled: boolean }): ReactElement {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    >
      <path d="m12 2.5 2.94 5.96 6.56.95-4.75 4.63 1.12 6.53L12 17.49l-5.87 3.08 1.12-6.53L2.5 9.41l6.56-.95L12 2.5Z" />
    </svg>
  );
}

/**
 * Lot 1 / 1C - marks or unmarks a product as one of the shop's "coups de
 * coeur", shown at the top of the storefront's home page.
 *
 * `variant="icon"` is the compact star of the product list;
 * `variant="button"` is the labelled button of the product page. Both call
 * the same narrow endpoint (never the full-replace product update), so a
 * click can only ever change this one fact.
 *
 * The server owns the limit: a 409 from this endpoint means the shop already
 * features the maximum, and is shown as a plain, actionable sentence rather
 * than a generic error. Nothing is pre-disabled on the client, so the UI can
 * never disagree with what the server would actually accept.
 */
export function FeaturedToggle({
  product,
  variant,
}: {
  product: Pick<Product, 'id' | 'name' | 'featuredAt'>;
  variant: 'icon' | 'button';
}): ReactElement {
  const { t } = useI18n();
  const toast = useToast();
  const mutation = useSetProductFeaturedMutation();
  const isFeatured = product.featuredAt !== null;
  const actionLabel = t(
    isFeatured ? 'merchant.products.featured.remove' : 'merchant.products.featured.add',
  );

  async function toggle(): Promise<void> {
    try {
      await mutation.mutateAsync({ productId: product.id, isFeatured: !isFeatured });
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        toast.error(t('merchant.products.featured.limitReached', { limit: MAX_FEATURED_PRODUCTS }));
      } else if (error instanceof ApiError && error.status !== 404) {
        toast.error(t(resolveErrorMessageKey(error)));
      } else {
        toast.error(t('merchant.products.featured.error'));
      }
    }
  }

  if (variant === 'button') {
    return (
      <Button
        variant={isFeatured ? 'secondary' : 'primary'}
        loading={mutation.isPending}
        aria-pressed={isFeatured}
        onClick={() => {
          void toggle();
        }}
      >
        <span className="inline-flex items-center gap-2">
          <StarIcon filled={isFeatured} />
          {actionLabel}
        </span>
      </Button>
    );
  }

  return (
    <button
      type="button"
      aria-pressed={isFeatured}
      aria-label={`${actionLabel} : ${product.name}`}
      title={actionLabel}
      disabled={mutation.isPending}
      onClick={() => {
        void toggle();
      }}
      className={`inline-flex h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-surface2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent disabled:opacity-50 ${
        isFeatured ? 'text-warn' : 'text-inkFaint'
      }`}
    >
      <StarIcon filled={isFeatured} />
    </button>
  );
}
